import { type ApolloError, useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  GetEmployeeDocument,
  type JobHistoryLevel,
  type JobHistoryType,
  useCreateEmployeeJobHistoryMutation,
  useDeleteEmployeeJobHistoryMutation,
  useUpdateEmployeeJobHistoryMutation,
} from '@/common/graphql';
import type { EmployeeJobHistoryType } from '@/common/types';
import { ApiUtils, StringUtils } from '@/common/utils';

interface JobHistoryPersistence {
  employeeId: string;
}

export const useJobHistoryPersistence = ({ employeeId }: JobHistoryPersistence) => {
  const client = useApolloClient();
  const onCompleted = () => {
    toast.success();
    client.refetchQueries({
      include: [GetEmployeeDocument],
    });
  };
  const onError = (error: ApolloError) => ApiUtils.handleApiError(error);

  const [createRecord] = useCreateEmployeeJobHistoryMutation({
    onCompleted,
    onError,
  });
  const [updateRecord] = useUpdateEmployeeJobHistoryMutation({
    onCompleted,
    onError,
  });
  const [deleteRecord] = useDeleteEmployeeJobHistoryMutation({
    onCompleted,
    onError,
  });

  const onJobHistoryCreate = useCallback(
    async ({ ...formData }: EmployeeJobHistoryType) => {
      const { data } = await createRecord({
        variables: {
          input: {
            level: formData.level as JobHistoryLevel,
            locationId: formData.locationId,
            businessUnitId: formData.businessUnitId,
            departmentId: formData.departmentId,
            effectiveDate: formData.effectiveDateStart,
            effectiveDateUntil: StringUtils.emptyToNull(formData.effectiveDateEnd),
            employeeId,
            jobId: formData.jobId,
            jobTitle: formData.jobTitle,
            team: formData.team,
            type: formData.type as JobHistoryType,
            regionalRole: formData.regionalRole,
            primaryManagerId: formData.primaryManagerId || null,
            dottedLineManagerId: formData.dottedLineManagerId || null,
            departmentLead: formData.departmentLead,
          },
        },
      });

      return data?.createEmployeeJobHistory !== undefined;
    },
    [createRecord, employeeId],
  );

  const onJobHistoryUpdate = useCallback(
    async (formData: EmployeeJobHistoryType) => {
      const { data } = await updateRecord({
        variables: {
          input: {
            level: formData.level as JobHistoryLevel,
            locationId: formData.locationId,
            businessUnitId: formData.businessUnitId,
            departmentId: formData.departmentId,
            effectiveDate: formData.effectiveDateStart,
            effectiveDateUntil: StringUtils.emptyToNull(formData.effectiveDateEnd),
            jobId: formData.jobId,
            jobTitle: formData.jobTitle,
            team: formData.team,
            type: formData.type as JobHistoryType,
            id: formData.id,
            regionalRole: formData.regionalRole,
            departmentLead: formData.departmentLead,
          },
        },
      });

      return data?.updateEmployeeJobHistory !== undefined;
    },
    [updateRecord],
  );

  const onJobHistoryRemove = useCallback(
    async (id: string) => {
      await deleteRecord({
        variables: { id: id },
      });
    },
    [deleteRecord],
  );

  return {
    onJobHistoryCreate,
    onJobHistoryUpdate,
    onJobHistoryRemove,
  };
};
